import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab,
  Alert,
  Divider,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import InventoryNavigation from '../components/InventoryNavigation';
import { 
  Inventory as InventoryIcon,
  Search as SearchIcon,
  AddCircle as ReceiveIcon,
  Assignment as AssignIcon,
  AssignmentReturn as ReturnIcon,
  QrCodeScanner as StatusIcon,
  CompareArrows as TransferIcon,
  QrCode as TemplateIcon,
} from '@mui/icons-material';
import InventorySummary from '../components/InventorySummary';
// Search functionality is now integrated into Summary
import EquipmentStatusAndActions from '../components/EquipmentStatusAndActions';
import BarcodeScanner from '../components/BarcodeScanner';
import QuickStackScanner from '../components/QuickStackScanner';
import EquipmentTemplateManager from '../components/EquipmentTemplateManager';
import WarehouseManager from '../components/WarehouseManager';
import ErrorBoundary from '../components/ErrorBoundary';
import { useInventory } from '../contexts/InventoryContext';

// Tab Panel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function InventoryPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const { inventory, loading, error, refreshInventory } = useInventory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL parameters for tab and equipment_id
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const equipmentId = params.get('equipment_id');

    // REDIRECT OLD QR CODES: If coming from old QR code with equipment_id,
    // redirect to Quick Actions page instead (makes old printed QR codes work)
    if (equipmentId && tab === 'status') {
      // Preserve all URL parameters when redirecting
      navigate(`/quick-actions?${params.toString()}`, { replace: true });
      return;
    }

    if (tab) {
      // Updated tab array - removed receive tab (now top-level)
      const tabIndex = ['summary', 'status', 'stacks', 'templates', 'warehouses'].indexOf(tab);
      if (tabIndex !== -1) {
        setCurrentTab(tabIndex);
      }
    } else {
      // Default to summary if no tab specified
      setCurrentTab(0);
    }

    // If equipment_id is provided, set it as search query to filter results
    if (equipmentId) {
      setSearchQuery(equipmentId);
      // If no specific tab was set, default to status tab for equipment management
      if (!tab) {
        setCurrentTab(1); // Status & Actions tab
        navigate(`/inventory?tab=status&equipment_id=${equipmentId}`, { replace: true });
      }
    }
  }, [location.search, navigate]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Update URL with the selected tab - removed receive tab
    const tabNames = ['summary', 'status', 'stacks', 'templates', 'warehouses'];
    navigate(`/inventory?tab=${tabNames[newValue]}`, { replace: true });
  };

  // Handle successful scan based on current tab
  const handleScanSuccess = (decodedText) => {
    switch (currentTab) {
      case 1: // Status & Actions tab
        // Will be handled by EquipmentStatusAndActions component
        break;
      case 2: // Stacks tab
        // Will be handled by QuickStackScanner component
        break;
      default:
        // Just set the search query for the summary tab
        setSearchQuery(decodedText);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Inventory Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <InventoryNavigation currentPage={['summary', 'status', 'stacks', 'templates', 'warehouses'][currentTab]}>
          {/* Tab Panels */}
          {currentTab === 0 && <InventorySummary />}
          {currentTab === 1 && <EquipmentStatusAndActions initialEquipmentId={searchQuery} />}
          {currentTab === 2 && <QuickStackScanner />}
          {currentTab === 3 && (
            <ErrorBoundary fallbackMessage="Error loading equipment templates. Please try refreshing the page.">
              <EquipmentTemplateManager />
            </ErrorBoundary>
          )}
          {currentTab === 4 && (
            <ErrorBoundary fallbackMessage="Error loading warehouse management. Please try refreshing the page.">
              <WarehouseManager />
            </ErrorBoundary>
          )}
        </InventoryNavigation>
      </Box>
    </Container>
  );
}